/**
 * Clinical AI Agent Panel - Main Component
 * 
 * Refactored modular AI-powered clinical assistant for hospice documentation,
 * assessment guidance, and care planning support.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Brain, Heart, Thermometer, GraduationCap, ArrowRightLeft, 
  Mic, UserCheck, Package, X
} from "lucide-react";
import { toast } from "sonner";

// Import sub-components
import DashboardTab from "./DashboardTab";
import { PainAssessmentForm, InfectionAssessmentForm, AssessmentResultDisplay } from "./AssessmentsTab";
import { TeachingForm, TeachingResultDisplay } from "./TeachingTab";
import { TransferForm, DischargeForm, ConditionChangeForm, WorkflowResultDisplay } from "./WorkflowsTab";
import VoiceTab from "./VoiceTab";
import AideTab from "./AideTab";
import ToolsTab from "./ToolsTab";
import { API_URL } from "./constants";

export default function ClinicalAIAgentPanel({ 
  isOpen, 
  onClose, 
  patientId, 
  patientName,
  onAssessmentComplete 
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [assessmentResult, setAssessmentResult] = useState(null);
  
  // Form states
  const [painForm, setPainForm] = useState({
    pain_location: "",
    pain_quality: "aching",
    pain_intensity: 5,
    aggravating_factors: "",
    relieving_factors: "",
    impact_function: "",
    impact_sleep: "",
    current_interventions: "",
    prn_use_last_24h: 0,
    clinical_notes: ""
  });
  
  const [infectionForm, setInfectionForm] = useState({
    signs_symptoms: [],
    onset_date: "",
    duration_hours: "",
    temp: "",
    hr: "",
    bp: "",
    wound_characteristics: "",
    urinary_symptoms: [],
    respiratory_symptoms: [],
    clinical_notes: ""
  });
  
  const [teachingForm, setTeachingForm] = useState({
    topic: "pain_management",
    audience: "caregiver",
    context: ""
  });
  
  const [transferForm, setTransferForm] = useState({
    transfer_type: "home_to_facility",
    from_location: "",
    to_location: "",
    reason: "",
    clinical_notes: ""
  });
  
  const [dischargeForm, setDischargeForm] = useState({
    discharge_type: "live_discharge",
    reason: "",
    clinical_notes: ""
  });
  
  const [conditionForm, setConditionForm] = useState({
    change_type: "symptom_escalation",
    description: "",
    severity: "moderate",
    clinical_notes: ""
  });
  
  const token = localStorage.getItem("token");
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/dashboard/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [patientId, token]);
  
  useEffect(() => {
    if (isOpen && patientId) {
      fetchDashboard();
    }
  }, [isOpen, patientId, fetchDashboard]);
  
  // Submit handlers
  const submitPainAssessment = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/assess/pain`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...painForm,
          aggravating_factors: painForm.aggravating_factors.split(",").map(s => s.trim()).filter(Boolean),
          relieving_factors: painForm.relieving_factors.split(",").map(s => s.trim()).filter(Boolean),
          current_interventions: painForm.current_interventions.split(",").map(s => s.trim()).filter(Boolean)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "pain", data });
        toast.success("Pain assessment completed");
        onAssessmentComplete?.("pain", data);
        fetchDashboard();
      } else {
        toast.error("Assessment failed");
      }
    } catch (error) {
      toast.error("Error submitting assessment");
    } finally {
      setLoading(false);
    }
  };
  
  const submitInfectionAssessment = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/assess/infection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          signs_symptoms: infectionForm.signs_symptoms,
          onset_date: infectionForm.onset_date || null,
          duration_hours: infectionForm.duration_hours ? parseInt(infectionForm.duration_hours) : null,
          vital_signs: {
            temp: infectionForm.temp ? parseFloat(infectionForm.temp) : null,
            hr: infectionForm.hr ? parseInt(infectionForm.hr) : null,
            bp: infectionForm.bp || null
          },
          urinary_symptoms: infectionForm.urinary_symptoms,
          respiratory_symptoms: infectionForm.respiratory_symptoms,
          clinical_notes: infectionForm.clinical_notes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "infection", data });
        toast.success("Infection assessment completed");
        onAssessmentComplete?.("infection", data);
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error submitting assessment");
    } finally {
      setLoading(false);
    }
  };
  
  const generateTeaching = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/teaching/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...teachingForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "teaching", data });
        toast.success("Teaching content generated");
      }
    } catch (error) {
      toast.error("Error generating teaching");
    } finally {
      setLoading(false);
    }
  };
  
  const runSafetyCheck = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/assess/safety-compliance?patient_id=${patientId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "safety", data });
        toast.success("Safety check completed");
        fetchDashboard();
      }
    } catch (error) {
      toast.error("Error running safety check");
    } finally {
      setLoading(false);
    }
  };
  
  const runCorrelation = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/correlate/symptoms?patient_id=${patientId}&days_lookback=14`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "correlation", data });
        toast.success("Symptom correlation complete");
      }
    } catch (error) {
      toast.error("Error running correlation");
    } finally {
      setLoading(false);
    }
  };
  
  const initiateTransfer = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/transfer/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...transferForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "transfer", data });
        toast.success("Transfer workflow initiated");
      }
    } catch (error) {
      toast.error("Error initiating transfer");
    } finally {
      setLoading(false);
    }
  };
  
  const initiateDischarge = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/discharge/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...dischargeForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "discharge", data });
        toast.success("Discharge workflow initiated");
      }
    } catch (error) {
      toast.error("Error initiating discharge");
    } finally {
      setLoading(false);
    }
  };
  
  const triggerConditionChange = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/condition-change/trigger`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...conditionForm
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult({ type: "condition_change", data });
        toast.success("Condition change workflow triggered");
      }
    } catch (error) {
      toast.error("Error triggering condition change");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className={`w-full sm:max-w-xl ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}
        data-testid="clinical-ai-agent-panel"
      >
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-cyan-500" />
              <SheetTitle className={isDark ? "text-white" : ""}>
                Clinical AI Agent
              </SheetTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <SheetDescription className={isDark ? "text-gray-400" : ""}>
            AI-powered clinical documentation assistance
            {patientName && <span className="ml-1 font-medium text-cyan-500">• {patientName}</span>}
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid grid-cols-7 mb-4 ${isDark ? "bg-slate-800" : ""}`}>
              <TabsTrigger value="dashboard" className="text-xs" data-testid="dashboard-tab">
                <Brain className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="pain" className="text-xs" data-testid="pain-tab">
                <Heart className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="infection" className="text-xs" data-testid="infection-tab">
                <Thermometer className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="teaching" className="text-xs" data-testid="teaching-tab">
                <GraduationCap className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="workflows" className="text-xs" data-testid="workflows-tab">
                <ArrowRightLeft className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="voice" className="text-xs" data-testid="voice-tab">
                <Mic className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="aide" className="text-xs" data-testid="aide-tab">
                <UserCheck className="h-3 w-3" />
              </TabsTrigger>
            </TabsList>
            
            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              <DashboardTab
                dashboard={dashboard}
                loading={loading}
                isDark={isDark}
                onRefresh={fetchDashboard}
                onRunSafetyCheck={runSafetyCheck}
                onRunCorrelation={runCorrelation}
              />
            </TabsContent>
            
            {/* Pain Assessment Tab */}
            <TabsContent value="pain">
              <PainAssessmentForm
                painForm={painForm}
                setPainForm={setPainForm}
                loading={loading}
                isDark={isDark}
                onSubmit={submitPainAssessment}
              />
              <AssessmentResultDisplay result={assessmentResult?.type === "pain" ? assessmentResult : null} isDark={isDark} />
            </TabsContent>
            
            {/* Infection Assessment Tab */}
            <TabsContent value="infection">
              <InfectionAssessmentForm
                infectionForm={infectionForm}
                setInfectionForm={setInfectionForm}
                loading={loading}
                isDark={isDark}
                onSubmit={submitInfectionAssessment}
              />
              <AssessmentResultDisplay result={assessmentResult?.type === "infection" ? assessmentResult : null} isDark={isDark} />
            </TabsContent>
            
            {/* Teaching Tab */}
            <TabsContent value="teaching">
              <TeachingForm
                teachingForm={teachingForm}
                setTeachingForm={setTeachingForm}
                loading={loading}
                isDark={isDark}
                onSubmit={generateTeaching}
              />
              <TeachingResultDisplay result={assessmentResult} isDark={isDark} />
            </TabsContent>
            
            {/* Workflows Tab */}
            <TabsContent value="workflows">
              <Tabs defaultValue="transfer" className="w-full">
                <TabsList className={`grid grid-cols-3 mb-4 ${isDark ? "bg-slate-800" : ""}`}>
                  <TabsTrigger value="transfer" className="text-xs">Transfer</TabsTrigger>
                  <TabsTrigger value="discharge" className="text-xs">Discharge</TabsTrigger>
                  <TabsTrigger value="condition" className="text-xs">Condition</TabsTrigger>
                </TabsList>
                
                <TabsContent value="transfer">
                  <TransferForm
                    transferForm={transferForm}
                    setTransferForm={setTransferForm}
                    loading={loading}
                    isDark={isDark}
                    onSubmit={initiateTransfer}
                  />
                </TabsContent>
                
                <TabsContent value="discharge">
                  <DischargeForm
                    dischargeForm={dischargeForm}
                    setDischargeForm={setDischargeForm}
                    loading={loading}
                    isDark={isDark}
                    onSubmit={initiateDischarge}
                  />
                </TabsContent>
                
                <TabsContent value="condition">
                  <ConditionChangeForm
                    conditionForm={conditionForm}
                    setConditionForm={setConditionForm}
                    loading={loading}
                    isDark={isDark}
                    onSubmit={triggerConditionChange}
                  />
                </TabsContent>
              </Tabs>
              <WorkflowResultDisplay result={assessmentResult} isDark={isDark} />
            </TabsContent>
            
            {/* Voice Tab */}
            <TabsContent value="voice">
              <VoiceTab
                patientId={patientId}
                loading={loading}
                setLoading={setLoading}
                isDark={isDark}
                onAssessmentComplete={onAssessmentComplete}
                token={token}
              />
            </TabsContent>
            
            {/* Aide Tab */}
            <TabsContent value="aide">
              <AideTab
                patientId={patientId}
                loading={loading}
                setLoading={setLoading}
                isDark={isDark}
                token={token}
              />
            </TabsContent>
          </Tabs>
          
          {/* Tools Button at Bottom */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setActiveTab("tools")}
              data-testid="tools-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Tools (Comfort Kit, Export)
            </Button>
          </div>
          
          {activeTab === "tools" && (
            <div className="mt-4">
              <ToolsTab
                patientId={patientId}
                loading={loading}
                setLoading={setLoading}
                isDark={isDark}
                token={token}
              />
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
